import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { adminService } from '../../services/adminService';
import { useAuth } from '../../context/authContext';
import { slugify, BLOG_STATUS, getPublishValidationErrors, isSlugValid } from '../../utils/blogHelpers';
import '../../styles/admin.css';

const QUILL_MODULES = {
  toolbar: [
    [{ header: [2, 3, false] }],
    ['bold', 'italic', 'underline'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link', 'image', 'blockquote', 'code-block'],
    ['clean']
  ]
};

const EMPTY_FORM = {
  title: '',
  slug: '',
  excerpt: '',
  content: '',
  featuredImageUrl: '',
  featuredImageAlt: '',
  categoryId: '',
  categoryName: '',
  tags: [],
  status: BLOG_STATUS.DRAFT,
  publishedAt: '',
  metaTitle: '',
  metaDescription: '',
  focusKeyword: '',
  canonicalUrl: '',
  ogImage: ''
};

const toDateInputValue = (value) => {
  const date = value?.toDate ? value.toDate() : (value ? new Date(value) : null);
  if (!date || Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 16);
};

const AdminBlogForm = () => {
  const { id } = useParams();
  const isEditing = Boolean(id);
  const navigate = useNavigate();
  const { user } = useAuth();

  const [form, setForm] = useState(EMPTY_FORM);
  const [slugTouched, setSlugTouched] = useState(false);
  const [tagInput, setTagInput] = useState('');
  const [categories, setCategories] = useState([]);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);

  const loadCategories = useCallback(async () => {
    try {
      const data = await adminService.getBlogCategories();
      setCategories(data);
    } catch (error) {
      console.error('Erreur lors du chargement des catégories :', error);
    }
  }, []);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  useEffect(() => {
    if (!isEditing) return;
    (async () => {
      try {
        const post = await adminService.getBlogPost(id);
        if (!post) {
          toast.error('Article introuvable');
          navigate('/admin/blog');
          return;
        }
        setForm({
          ...EMPTY_FORM,
          ...post,
          tags: Array.isArray(post.tags) ? post.tags : [],
          publishedAt: toDateInputValue(post.publishedAt)
        });
        setSlugTouched(true);
      } catch (error) {
        console.error('Erreur lors du chargement de l\'article :', error);
        toast.error('Impossible de charger l\'article');
      } finally {
        setLoading(false);
      }
    })();
  }, [id, isEditing, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (name === 'title' && !slugTouched) {
      setForm((prev) => ({ ...prev, title: value, slug: slugify(value) }));
    }
  };

  const handleSlugChange = (e) => {
    setSlugTouched(true);
    setForm((prev) => ({ ...prev, slug: slugify(e.target.value) }));
  };

  const handleContentChange = (html) => {
    setForm((prev) => ({ ...prev, content: html }));
  };

  const handleAddTag = (e) => {
    e.preventDefault();
    const value = tagInput.trim();
    if (!value) return;
    if (!form.tags.includes(value)) {
      setForm((prev) => ({ ...prev, tags: [...prev.tags, value] }));
    }
    setTagInput('');
  };

  const handleRemoveTag = (tag) => {
    setForm((prev) => ({ ...prev, tags: prev.tags.filter((t) => t !== tag) }));
  };

  const handleCategoryChange = (e) => {
    const categoryId = e.target.value;
    const category = categories.find((c) => c.id === categoryId);
    setForm((prev) => ({ ...prev, categoryId, categoryName: category?.name || '' }));
  };

  const handleAddCategory = async (e) => {
    e.preventDefault();
    const name = newCategoryName.trim();
    if (!name) return;
    try {
      const category = await adminService.createBlogCategory(name);
      setCategories((prev) => [...prev.filter((c) => c.id !== category.id), category]);
      setForm((prev) => ({ ...prev, categoryId: category.id, categoryName: category.name }));
      setNewCategoryName('');
      toast.success('Catégorie ajoutée');
    } catch (error) {
      console.error('Erreur lors de la création de la catégorie :', error);
      toast.error('Impossible de créer la catégorie');
    }
  };

  const buildPayload = (status) => ({
    ...form,
    status,
    publishedAt: form.publishedAt ? new Date(form.publishedAt).toISOString() : (status === BLOG_STATUS.PUBLISHED ? new Date().toISOString() : form.publishedAt || null),
    authorId: user?.uid || form.authorId || null,
    authorName: user?.username || user?.email || form.authorName || 'Admin'
  });

  const handleSave = async (status) => {
    if (!form.title.trim()) {
      toast.error('Le titre est requis');
      return;
    }
    if (!isSlugValid(form.slug)) {
      toast.error('Le slug est invalide (lettres minuscules, chiffres et tirets uniquement)');
      return;
    }

    setSaving(true);
    try {
      const slugTaken = await adminService.isBlogSlugTaken(form.slug, isEditing ? id : null);
      if (slugTaken) {
        toast.error('Ce slug est déjà utilisé par un autre article');
        setSaving(false);
        return;
      }

      const payload = buildPayload(status);

      if (status === BLOG_STATUS.PUBLISHED || status === BLOG_STATUS.SCHEDULED) {
        const errors = getPublishValidationErrors(payload);
        if (Object.keys(errors).length > 0) {
          toast.error(`Champs requis pour publier : ${Object.keys(errors).join(', ')}`);
          setSaving(false);
          return;
        }
      }

      if (isEditing) {
        await adminService.updateBlogPost(id, payload);
        toast.success('✅ Article mis à jour');
      } else {
        await adminService.createBlogPost(payload);
        toast.success('🎉 Article créé');
      }
      navigate('/admin/blog');
    } catch (error) {
      console.error('Erreur lors de l\'enregistrement de l\'article :', error);
      toast.error('❌ Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-products">
        <div className="container">
          <div className="loading-container">
            <div className="loading-spinner"><div className="spinner"></div></div>
            <p>Chargement...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-products">
      <div className="container">
        <div className="admin-header">
          <h1>
            <i className={`fas ${isEditing ? 'fa-edit' : 'fa-plus'}`}></i>
            {isEditing ? 'Modifier l\'article' : 'Nouvel article'}
          </h1>
          <div className="breadcrumb">
            <Link to="/admin/dashboard">
              <i className="fas fa-tachometer-alt"></i> Tableau de bord
            </Link>
            <span>/</span>
            <Link to="/admin/blog">Blog</Link>
            <span>/</span> {isEditing ? 'Modifier' : 'Nouveau'}
          </div>
        </div>

        <div className="product-form-section">
          <div className="form-card">
            <form onSubmit={(e) => e.preventDefault()} className="product-form">
              <div className="form-group">
                <label htmlFor="title"><i className="fas fa-heading"></i> Titre *</label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={form.title}
                  onChange={handleChange}
                  required
                  placeholder="Titre de l'article"
                />
              </div>

              <div className="form-group">
                <label htmlFor="slug"><i className="fas fa-link"></i> Slug *</label>
                <input
                  type="text"
                  id="slug"
                  name="slug"
                  value={form.slug}
                  onChange={handleSlugChange}
                  required
                  placeholder="titre-de-larticle"
                />
                <small>URL publique : /blog/{form.slug || '...'}</small>
              </div>

              <div className="form-group">
                <label htmlFor="excerpt"><i className="fas fa-align-left"></i> Résumé / extrait</label>
                <textarea
                  id="excerpt"
                  name="excerpt"
                  value={form.excerpt}
                  onChange={handleChange}
                  rows="3"
                  placeholder="Court résumé affiché sur les cartes d'articles"
                />
              </div>

              <div className="form-group">
                <label><i className="fas fa-file-alt"></i> Contenu *</label>
                <ReactQuill
                  theme="snow"
                  value={form.content}
                  onChange={handleContentChange}
                  modules={QUILL_MODULES}
                />
              </div>

              <div className="form-row">
                <div className="form-group" style={{ flex: 1 }}>
                  <label htmlFor="featuredImageUrl"><i className="fas fa-image"></i> Image mise en avant (URL)</label>
                  <input
                    type="url"
                    id="featuredImageUrl"
                    name="featuredImageUrl"
                    value={form.featuredImageUrl}
                    onChange={handleChange}
                    placeholder="https://..."
                  />
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label htmlFor="featuredImageAlt"><i className="fas fa-comment-alt"></i> Texte alternatif de l'image</label>
                  <input
                    type="text"
                    id="featuredImageAlt"
                    name="featuredImageAlt"
                    value={form.featuredImageAlt}
                    onChange={handleChange}
                    placeholder="Description de l'image pour le SEO/accessibilité"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group" style={{ flex: 1 }}>
                  <label htmlFor="categoryId"><i className="fas fa-bookmark"></i> Catégorie</label>
                  <select id="categoryId" name="categoryId" value={form.categoryId} onChange={handleCategoryChange}>
                    <option value="">Aucune catégorie</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                  <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                    <input
                      type="text"
                      placeholder="Nouvelle catégorie..."
                      value={newCategoryName}
                      onChange={(e) => setNewCategoryName(e.target.value)}
                    />
                    <button type="button" className="btn btn-sm btn-outline" onClick={handleAddCategory}>
                      <i className="fas fa-plus"></i>
                    </button>
                  </div>
                </div>

                <div className="form-group" style={{ flex: 1 }}>
                  <label htmlFor="tagInput"><i className="fas fa-tags"></i> Tags</label>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <input
                      type="text"
                      id="tagInput"
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleAddTag(e); }}
                      placeholder="Ajouter un tag et appuyer sur Entrée"
                    />
                    <button type="button" className="btn btn-sm btn-outline" onClick={handleAddTag}>
                      <i className="fas fa-plus"></i>
                    </button>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.5rem' }}>
                    {form.tags.map((tag) => (
                      <span key={tag} className="stock-badge in-stock">
                        {tag}
                        <button
                          type="button"
                          onClick={() => handleRemoveTag(tag)}
                          style={{ background: 'none', border: 'none', color: 'inherit', marginLeft: '0.4rem', cursor: 'pointer' }}
                        >
                          <i className="fas fa-times"></i>
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group" style={{ flex: 1 }}>
                  <label htmlFor="status"><i className="fas fa-flag"></i> Statut</label>
                  <select id="status" name="status" value={form.status} onChange={handleChange}>
                    <option value={BLOG_STATUS.DRAFT}>Brouillon</option>
                    <option value={BLOG_STATUS.PUBLISHED}>Publié</option>
                    <option value={BLOG_STATUS.SCHEDULED}>Programmé</option>
                  </select>
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label htmlFor="publishedAt"><i className="fas fa-calendar"></i> Date de publication</label>
                  <input
                    type="datetime-local"
                    id="publishedAt"
                    name="publishedAt"
                    value={form.publishedAt}
                    onChange={handleChange}
                  />
                  <small>Laissez vide pour utiliser la date/heure actuelle lors de la publication</small>
                </div>
              </div>

              <h3 style={{ marginTop: '1.5rem' }}><i className="fas fa-search"></i> Référencement (SEO)</h3>

              <div className="form-group">
                <label htmlFor="metaTitle">Titre SEO (balise &lt;title&gt;)</label>
                <input
                  type="text"
                  id="metaTitle"
                  name="metaTitle"
                  value={form.metaTitle}
                  onChange={handleChange}
                  placeholder="Par défaut : titre de l'article"
                />
              </div>

              <div className="form-group">
                <label htmlFor="metaDescription">Meta description *</label>
                <textarea
                  id="metaDescription"
                  name="metaDescription"
                  value={form.metaDescription}
                  onChange={handleChange}
                  rows="2"
                  placeholder="Description affichée dans les résultats de recherche (requise pour publier)"
                />
              </div>

              <div className="form-row">
                <div className="form-group" style={{ flex: 1 }}>
                  <label htmlFor="focusKeyword">Mot-clé principal</label>
                  <input
                    type="text"
                    id="focusKeyword"
                    name="focusKeyword"
                    value={form.focusKeyword}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label htmlFor="canonicalUrl">URL canonique (optionnel)</label>
                  <input
                    type="url"
                    id="canonicalUrl"
                    name="canonicalUrl"
                    value={form.canonicalUrl}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="ogImage">Image Open Graph (optionnel)</label>
                <input
                  type="url"
                  id="ogImage"
                  name="ogImage"
                  value={form.ogImage}
                  onChange={handleChange}
                  placeholder="Par défaut : image mise en avant"
                />
              </div>

              <div className="form-actions">
                <Link to="/admin/blog" className="btn btn-outline">
                  <i className="fas fa-times"></i> Annuler
                </Link>
                <button type="button" className="btn btn-outline" disabled={saving} onClick={() => handleSave(BLOG_STATUS.DRAFT)}>
                  <i className="fas fa-save"></i> Enregistrer le brouillon
                </button>
                <button type="button" className="btn btn-primary" disabled={saving} onClick={() => handleSave(BLOG_STATUS.PUBLISHED)}>
                  <i className="fas fa-upload"></i> {saving ? 'Enregistrement...' : 'Publier'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminBlogForm;
